/**
 * PersonalInfoSection
 *
 * Renders the citizen's personal info block (name, phone, DOB,
 * avatar, address) — read-only or with an edit pencil depending on
 * whether `onEdit` is provided.
 */

import { useLocale, useTranslation } from '@tonnie/locale';
import { User, Phone, Calendar, MapPin } from '@tonnie/assets';
import { UserAvatar } from '@tonnie/ui';
import { ProfileSectionCard } from '../ProfileSectionCard';
import { IOnboardingData, ProfileReviewEditHandler, type ProfileViewer } from '../types';

interface IPersonalInfoSectionProps {
  data: IOnboardingData;
  viewer: ProfileViewer;
  onEdit?: ProfileReviewEditHandler;
}

export function PersonalInfoSection({ data, viewer, onEdit }: IPersonalInfoSectionProps) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const fullName = [data.firstName, data.lastName].filter(Boolean).join(' ');
  // Beside a company's view of the photo the name is already said.
  const showNameField = viewer === 'self' || !data.avatarUrl;

  return (
    <ProfileSectionCard
      icon={User}
      title={t('profile.personalInfo')}
      description={
        viewer === 'self'
          ? t('profile.personalInfoDescription')
          : t('profile.review.company.personalInfoDescription')
      }
      stepId="personal"
      onEdit={onEdit}
    >
      {data.avatarUrl && (
        <div className="flex items-center gap-4 pb-4 border-b border-border/50">
          <UserAvatar
            src={data.avatarUrl}
            name={fullName}
            alt={t('profile.profilePhoto')}
            size="xl"
            className="border-2 border-primary/20 shadow-md"
          />
          {viewer === 'self' ? (
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                {t('profile.profilePhoto')}
              </p>
              {/* "Photo set" is the citizen's own status line. */}
              <p className="text-sm text-muted-foreground">{t('profile.photoSet')}</p>
            </div>
          ) : (
            // A company sees who it is reading: the person beside their photo,
            // not a "Profile photo" caption. The name field below then goes.
            <p
              className="text-lg font-semibold text-foreground"
              data-testid="profile-identity-name"
            >
              {fullName}
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {showNameField && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <User className="w-3 h-3" />
              {t('profile.fullName')}
            </p>
            <p className="text-base font-semibold text-foreground">{fullName}</p>
          </div>
        )}

        {data.dateOfBirth && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Calendar className="w-3 h-3" />
              {t('profile.dateOfBirth')}
            </p>
            <p className="text-base font-medium text-foreground">
              {data.dateOfBirth ? formatDate(data.dateOfBirth, 'long') : ''}
            </p>
          </div>
        )}

        {data.phone && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Phone className="w-3 h-3" />
              {t('profile.phone')}
            </p>
            <p className="text-base font-medium text-foreground">
              {data.phoneCountryCode} {data.phone}
            </p>
          </div>
        )}
      </div>

      {data.address && (
        <div className="space-y-2 pt-4 border-t border-border/50">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
            <MapPin className="w-3 h-3" />
            {t('profile.address')}
          </p>
          <div className="bg-muted/30 rounded-lg p-3 space-y-1.5">
            {data.address.formattedAddress && (
              <p className="text-sm font-medium text-foreground">{data.address.formattedAddress}</p>
            )}
          </div>
        </div>
      )}
    </ProfileSectionCard>
  );
}
